// The five numbered boxes of the New Shipment form. Each takes the whole form and a `set` that changes part of it.
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { FormField } from '../../../components/FormField';
import { StatusChip } from '../../../components/StatusChip';
import { Panel } from '../../../components/Panel';
import { useFormLists } from '../useShipments';
import { MODES, PACKAGE_UNITS, SHIPMENT_TYPES, type ShipmentForm } from './shipmentForm';

export type SectionProps = { form: ShipmentForm; set: (patch: Partial<ShipmentForm>) => void };

const cols = (n: number) => ({
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `repeat(${n}, minmax(0, 1fr))` },
});

export function BasicSection({ form, set }: SectionProps) {
  const { customerNames } = useFormLists();
  return (
    <Panel title="1. Basic Information">
      <Box sx={cols(4)}>
        <FormField label="SRB Code" required value={form.srb} onChange={(srb) => set({ srb })} />
        <FormField
          label="Customer"
          required
          value={form.customer}
          onChange={(customer) => set({ customer })}
          options={customerNames}
        />
        <FormField label="Contact Person" value={form.contact} onChange={(contact) => set({ contact })} />
        <FormField label="Email" type="email" value={form.email} onChange={(email) => set({ email })} />
        <FormField
          label="Shipment Type"
          required
          value={form.shipmentType}
          onChange={(shipmentType) => set({ shipmentType })}
          options={SHIPMENT_TYPES}
        />
        <FormField label="Mode" required value={form.mode} onChange={(mode) => set({ mode })} options={MODES} />
        <Stack>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box sx={{ pt: 0.75 }}>
            <StatusChip label="RFQ" tone="neutral" />
          </Box>
        </Stack>
        <FormField label="Created Date" type="date" value={form.created} onChange={(created) => set({ created })} />
        <FormField label="Operator" value={form.operator} onChange={(operator) => set({ operator })} />
      </Box>
    </Panel>
  );
}

export function RouteTradeSection({ form, set }: SectionProps) {
  const { places, cargoTypes } = useFormLists();
  return (
    <Panel title="2. Route & Trade Details">
      <Box sx={cols(4)}>
        <FormField
          label="Origin"
          required
          value={form.origin}
          onChange={(origin) => set({ origin })}
          options={places}
        />
        <FormField
          label="Destination"
          required
          value={form.destination}
          onChange={(destination) => set({ destination })}
          options={places}
        />
        <FormField
          label="Incoterm"
          required
          value={form.incoterm}
          onChange={(incoterm) => set({ incoterm })}
          options={['EXW', 'FCA', 'FOB', 'CIF', 'CPT', 'CIP', 'DAP', 'DDP']}
        />
        <FormField
          label="Commodity Description"
          required
          value={form.commodity}
          onChange={(commodity) => set({ commodity })}
          options={cargoTypes}
        />
      </Box>
    </Panel>
  );
}

export function CargoSection({ form, set }: SectionProps) {
  const unit = (text: string) => ({ input: { endAdornment: <InputAdornment position="end">{text}</InputAdornment> } });
  return (
    <Panel title="3. Cargo Details">
      <Box sx={cols(4)}>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Total Packages"
            required
            value={form.packages}
            onChange={(e) => set({ packages: e.target.value })}
          />
          <FormField
            label="Unit"
            value={form.packageUnit}
            onChange={(packageUnit) => set({ packageUnit })}
            options={PACKAGE_UNITS}
          />
        </Stack>
        <TextField
          label="Gross Weight"
          required
          value={form.grossWeight}
          onChange={(e) => set({ grossWeight: e.target.value })}
          slotProps={unit('kg')}
        />
        <TextField
          label="Total Volume"
          required
          value={form.volume}
          onChange={(e) => set({ volume: e.target.value })}
          slotProps={unit('CBM')}
        />
        <Stack direction="row" spacing={0.5}>
          <TextField label="Length" value={form.length} onChange={(e) => set({ length: e.target.value })} />
          <TextField label="Width" value={form.width} onChange={(e) => set({ width: e.target.value })} />
          <TextField label="Height (cm)" value={form.height} onChange={(e) => set({ height: e.target.value })} />
        </Stack>
      </Box>
      <Box sx={{ ...cols(3), mt: 1 }}>
        <Choice
          label="Stackability"
          value={form.stackable}
          options={['Stackable', 'Non-stackable']}
          onChange={(stackable) => set({ stackable })}
        />
        <Choice
          label="Dangerous Goods (DG)"
          value={form.dangerous}
          options={['Yes', 'No']}
          onChange={(dangerous) => set({ dangerous })}
        />
        <TextField
          label="Cargo Description"
          required
          multiline
          minRows={3}
          value={form.cargoDescription}
          onChange={(e) => set({ cargoDescription: e.target.value })}
        />
      </Box>
    </Panel>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: T[];
  onChange: (v: T) => void;
}) {
  return (
    <FormControl>
      <FormLabel>{label} *</FormLabel>
      <RadioGroup value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <FormControlLabel key={o} value={o} control={<Radio />} label={o} />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

export function ExtraSection({ form, set }: SectionProps) {
  return (
    <Panel title="4. Additional Requirements">
      <Box sx={cols(3)}>
        <FormField
          label="Cargo Ready Date"
          type="date"
          value={form.readyDate}
          onChange={(readyDate) => set({ readyDate })}
        />
        <TextField
          label="Service Requirements (optional)"
          placeholder="e.g. direct service, specific carrier, temperature control"
          value={form.serviceRequirements}
          onChange={(e) => set({ serviceRequirements: e.target.value })}
        />
        <TextField
          label="Special Instructions (optional)"
          placeholder="e.g. keep upright, notify before arrival"
          value={form.specialInstructions}
          onChange={(e) => set({ specialInstructions: e.target.value })}
        />
      </Box>
    </Panel>
  );
}

export function NotesSection({ form, set }: SectionProps) {
  return (
    <Panel title="5. Internal Notes & Attachments">
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <TextField
          label="Internal Notes (optional)"
          placeholder="Add any internal notes, customer requirements, or follow-ups…"
          multiline
          minRows={4}
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
        />
        <Button
          component="label"
          variant="outlined"
          color="inherit"
          sx={{ borderStyle: 'dashed', flexDirection: 'column', gap: 0.5, minHeight: 110 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'primary.main' }}>
            <UploadFileOutlined />
            <span>Upload files</span>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {form.files.length ? form.files.join(', ') : 'BL, PL, CI, specs, photos, etc.'}
          </Typography>
          <input
            hidden
            multiple
            type="file"
            onChange={(e) => set({ files: [...(e.target.files ?? [])].map((f) => f.name) })}
          />
        </Button>
      </Box>
    </Panel>
  );
}
